import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import { LatLng, ZoneRegion, zoneFor } from './zone';

/**
 * Zone 1 drawn over a real map.
 *
 * The boundary comes from `zone1.json` — the same file the zone test reads — so
 * what is drawn can never disagree with the in/out answer beside it. Only the
 * basemap underneath is Google's; the shape is ours.
 *
 * The Maps script is already on the page for the address autocomplete, so this
 * costs no extra key and no extra load.
 */

type Props = {
  /** The place of death, if one has been chosen. */
  destination?: LatLng | null;
  /** Which province's zone to draw. */
  region: ZoneRegion;
  /** The border point it was measured from, when out of zone. */
  borderPoint?: LatLng | null;
  /** The road path actually measured, when a driving route was found. */
  routePath?: LatLng[] | null;
  /**
   * Whether the Maps script has resolved. A dependency, not just a guard:
   * without it the effect runs once against a missing `window.google`, bails,
   * and never runs again — leaving a permanently blank box.
   */
  ready: boolean;
  height?: number;
};

type Ring = [number, number][];

const ringsOf = (feature: any): Ring[] => {
  const geom = feature.geometry;
  const polygons = geom.type === 'MultiPolygon' ? geom.coordinates : [geom.coordinates];
  // Outer rings only — a hole is interior to the zone, and at this scale would
  // read as a gap the boundary does not actually have.
  return polygons.map((polygon: Ring[]) => polygon[0]);
};

export const ZoneMap = ({ destination, borderPoint, routePath, ready, region, height = 420 }: Props) => {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<any>(null);
  // Everything drawn on top of the basemap, so it can be cleared and redrawn
  // when the selected address changes without rebuilding the map itself.
  const overlays = useRef<any[]>([]);

  useEffect(() => {
    const g = window.google;
    if (!ready || !g?.maps || !container.current) return;

    if (!map.current) {
      map.current = new g.maps.Map(container.current, {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        // The zone is the subject; points of interest are noise behind it.
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      });
    }

    overlays.current.forEach((o) => o.setMap(null));
    overlays.current = [];

    const bounds = new g.maps.LatLngBounds();

    zoneFor(region).features.forEach((feature: any) => {
      ringsOf(feature).forEach((ring) => {
        const path = ring.map(([lng, lat]) => {
          bounds.extend({ lat, lng });
          return { lat, lng };
        });
        const polygon = new g.maps.Polygon({
          paths: path,
          strokeColor: '#1976d2',
          strokeOpacity: 0.9,
          strokeWeight: 1.5,
          fillColor: '#1976d2',
          // Light enough that the streets underneath stay readable — the point
          // of a basemap is being able to see it.
          fillOpacity: 0.12,
          clickable: false,
          map: map.current,
        });
        overlays.current.push(polygon);
      });
    });

    if (destination) {
      bounds.extend(destination);
      overlays.current.push(
        new g.maps.Marker({
          position: destination,
          map: map.current,
          title: 'Place of death',
        }),
      );
    }

    if (destination && borderPoint) {
      bounds.extend(borderPoint);
      overlays.current.push(
        new g.maps.Marker({
          position: borderPoint,
          map: map.current,
          title: 'Measured from the zone border here',
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#d32f2f',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 1.5,
          },
        }),
      );
      // The route actually measured where routing succeeded; a straight line
      // otherwise, so the picture never implies a road we did not price.
      const measured = routePath && routePath.length > 1;
      overlays.current.push(
        new g.maps.Polyline({
          path: measured ? routePath : [borderPoint, destination],
          strokeColor: '#d32f2f',
          strokeWeight: measured ? 4 : 2,
          // Google has no strokeDasharray; a dashed line is a transparent
          // stroke with repeating dash symbols drawn along it.
          strokeOpacity: measured ? 0.9 : 0,
          icons: measured
            ? undefined
            : [
                {
                  icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeWeight: 2, scale: 3 },
                  offset: '0',
                  repeat: '12px',
                },
              ],
          map: map.current,
        }),
      );
      if (measured) routePath.forEach((p) => bounds.extend(p));
    }

    map.current.fitBounds(bounds, 24);
  }, [destination, borderPoint, routePath, ready, region]);

  return (
    <Box
      ref={container}
      sx={{
        height,
        width: '100%',
        borderRadius: 1,
        border: (t) => `1px solid ${t.palette.divider}`,
        // Until the Maps script resolves there is nothing to show; a plain
        // surface beats a collapsed empty box.
        bgcolor: 'action.hover',
      }}
    />
  );
};

export default ZoneMap;
